import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { LiveFeed } from './LiveFeed';
import { useTodaysMatchesQuery } from '@data/matches/useTodaysMatchesQuery';
import { useIsMobile } from '../../utils/useIsMobile';
import { ClubNameType } from '@ui/fixtures/contracts';
import FixtureListUI from '@ui/fixtures/components/FixtureList/FixtureListUI';
import { Link, LinkSize, LinkVariant } from '@ui/common/components/Link';
import { SocketProvider } from './SocketProvider';
import { Loader } from '@ui/common/components/Loader/Loader';
import { Helmet } from 'react-helmet-async';
import { ExpandIcon } from '@ui/common/components/ExpandIcon';
import { CollapseIcon } from '@ui/common/components/CollapseIcon';

export function MatchCentrePageUI() {
  const fixturesResponse = useTodaysMatchesQuery({ withLiveScores: true });
  const isMobile = useIsMobile();
  const [allFixturesExpanded, setAllFixturesExpanded] = useState<boolean>(false);

  const fixtures = fixturesResponse.data?.items ?? [];

  const toggleAllFixtures = () => {
    setAllFixturesExpanded(!allFixturesExpanded);
  };

  const competitionIds = useMemo(
    () =>
      fixturesResponse.data?.items
        ?.reduce((acc, fixture) => {
          if (!acc.includes(fixture.competition.id)) {
            acc.push(fixture.competition.id);
          }
          return acc;
        }, [] as string[])
        .sort(),
    [fixturesResponse.data?.items]
  );

  // Shows an alert when user tries to manually refresh page
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Show a confirmation dialog
      event.preventDefault();
      event.returnValue = ''; // Required for Chrome
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <div className="match-centre">
      {/* <div className="match-centre__title-container">
        <h1 className="match-centre__title">Match Centre</h1>
      </div> */}
      <div className="match-centre__content">
        {fixtures.length === 0 && fixturesResponse.isFetched && (
          <div className="match-centre__empty-state-container">
            <h3 className="match-centre__title">NO GAMES TODAY</h3>
            <Link
              url="/fixtures"
              label="Find Fixtures"
              variant={LinkVariant.Contained}
              size={LinkSize.m}
              openInNewTab={false}
            />
          </div>
        )}
        {!fixturesResponse.isFetched && (
          <div className="match-centre__loader-container">
            <Loader size={120} />
          </div>
        )}
        {fixturesResponse.isFetched && fixtures.length > 0 && (
          <>
            <div className="match-centre__component-container">
              <div className="match-centre__component-title-container">
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <h2 id="live-ticker">{isMobile ? 'LIVE TICKER' : 'Live ticker'}</h2>
                  <span className="match-centre__live-ticker-note">*auto-updates</span>
                </div>
                {isMobile && (
                  <Link
                    url="#todays-games"
                    label="to Today's Games"
                    variant={LinkVariant.Text}
                    size={LinkSize.m}
                    openInNewTab={false}
                  />
                )}
              </div>
              <LiveFeed />
            </div>
            <div className="match-centre__component-container">
              <div className="match-centre__component-title-container">
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <h2 id="todays-games">Today's games</h2>
                  <span className="match-centre__live-ticker-note">*auto-updates</span>
                </div>

                {isMobile && (
                  <>
                    <button
                      className={cn(`match-centre__expand-button`, `link-button-text`, `link-button-text--sm`)}
                      onClick={toggleAllFixtures}
                      aria-label={allFixturesExpanded ? 'Collapse all fixtures' : 'Expand all fixtures'}
                      title={allFixturesExpanded ? 'Collapse all' : 'Expand all'}
                    >
                      {allFixturesExpanded ? (
                        <CollapseIcon className="match-centre__expand-icon" size={18} />
                      ) : (
                        <ExpandIcon className="match-centre__expand-icon" size={18} />
                      )}
                    </button>
                    <Link
                      url="#live-ticker"
                      label="to Live Ticker"
                      variant={LinkVariant.Text}
                      size={LinkSize.m}
                      openInNewTab={false}
                    />
                  </>
                )}

                {!isMobile && (
                  <button
                    className={cn(`match-centre__expand-button`, `link-button-text`, `link-button-text--sm`)}
                    onClick={toggleAllFixtures}
                    aria-label={allFixturesExpanded ? 'Collapse all fixtures' : 'Expand all fixtures'}
                    title={allFixturesExpanded ? 'Collapse all' : 'Expand all'}
                  >
                    {allFixturesExpanded ? (
                      <CollapseIcon
                        className="match-centre__expand-icon match-centre__expand-icon--desktop"
                        size={18}
                      />
                    ) : (
                      <ExpandIcon className="match-centre__expand-icon match-centre__expand-icon--desktop" size={18} />
                    )}
                    <span>{allFixturesExpanded ? 'Collapse all' : 'Expand all'}</span>
                  </button>
                )}
              </div>
              <FixtureListUI
                className="matches-list__matches-container"
                fixtures={fixturesResponse.data?.items ?? []}
                columns={1}
                showAttendance={!isMobile}
                groupByDate
                groupByCompetition
                nameType={ClubNameType.ShortName}
                competitionsSort={competitionIds}
                isFixtureExpandable
                sortByDate={'ASC'}
                allFixturesExpanded={allFixturesExpanded}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function MatchCentrePage() {
  const metaTitle = `${import.meta.env.VITE_SITE_NAME} Fixtures & Live Matches Season 2025/26`;
  const metaDescription = `View the ${
    import.meta.env.VITE_ENTITY_NAME
  } fixtures for the 2025/26 season, visit the official website of the ${import.meta.env.VITE_SITE_NAME}.`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <SocketProvider>
        <MatchCentrePageUI />
      </SocketProvider>
    </>
  );
}
